import i18n from '../../i18n/i18n';
import { tr } from '../../i18n/i18n';
/**
 * Dashboard Redux Actions (Async Thunks)
 * 
 * This file contains async thunks for dashboard API calls:
 * 1. fetchDashboardSummary - Fetches Row 2/3 data (summary metrics)
 * 2. fetchStudentAttendance - Fetches Row 4 data (attendance with date range)
 * 
 * Note: The Redux slice is defined in dashboard.slice.ts (separate file)
 * 
 * @module dashboard.action
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { getDashboardSummary, getStudentAttendance } from '../../services/dashboard';
import {

  IGetDashboardSummaryRequestParams,
  IGetStudentAttendanceRequestParams,
  IDashboardSummaryResponse,
} from '../../interface/dashboard.interface';

// ==================== Error Interfaces ====================

interface IDashboardError {
  status: number;
  message: string;
  data?: any;
}

// ==================== Async Thunks ====================

/**
 * Fetch Dashboard Summary (Row 2/3 data)
 * 
 * Fetches dashboard summary data including:
 * - activeStudents (filterable by Row 2)
 * - activeTeachers (NOT filterable - global)
 * - activeStaff (NOT filterable - global)
 * - pendingInvoices (filterable by Row 2)
 * - upcomingEventsCount (filterable by Row 2)
 * - studentAttendance (separate filter in Row 4, but returned here for initial load)
 * 
 * Endpoint: GET /v2/dashboard
 * Query Params: curriculumId, gradeId, classroomId (optional)
 * 
 * @param params - Row 2 filter parameters (curriculum/grade/classroom)
 * @returns Dashboard summary response
 * 
 * @example
 * ```typescript
 * // Fetch with filters
 * dispatch(fetchDashboardSummary({
 *   curriculumId: 'curr-123',
 *   gradeId: 'grade-456',
 *   classroomId: 'class-789'
 * }));
 * 
 * // Fetch without filters (all data)
 * dispatch(fetchDashboardSummary({}));
 * ```
 */
export const fetchDashboardSummary = createAsyncThunk<
  IDashboardSummaryResponse,
  IGetDashboardSummaryRequestParams,
  { rejectValue: IDashboardError }
>(
  'dashboard/fetchDashboardSummary',
  async (params, { rejectWithValue }) => {
    try {
      // Call service layer with typed params
      const response = await getDashboardSummary(params || {});
      
      return response.data as IDashboardSummaryResponse;
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const error = err as AxiosError<IDashboardError>;
        const errorResponse = error.response?.data;
        
        return rejectWithValue(
          errorResponse || {
            status: error.response?.status || 500,
            message: error.message || i18n.t('toast.failed_to_fetch_dashboard_summary', 'Failed to fetch dashboard summary'),
          }
        );
      }
      
      return rejectWithValue({
        status: 500,
        message: i18n.t('msg.an_unexpected_error_occurred', i18n.t('toast.an_unexpected_error_occurred', 'An unexpected error occurred')),
      });
    }
  }
);

/**
 * Fetch Student Attendance (Row 4 data)
 * 
 * Fetches student attendance data with optional date range filter.
 * This data is INDEPENDENT from Row 2 filters (curriculum/grade/classroom).
 * 
 * Endpoint: GET /v2/student-attendance (or /v2/dashboard with date params if backend combines them)
 * Query Params: startDate, endDate (optional, format: YYYY-MM-DD)
 * 
 * Returns:
 * - present: number
 * - absent: number
 * - leave: number
 * - pending: number
 * 
 * @param params - Row 4 date range parameters (startDate/endDate)
 * @returns Student attendance response
 * 
 * @example
 * ```typescript
 * // Fetch with date range
 * dispatch(fetchStudentAttendance({
 *   startDate: '2024-01-01',
 *   endDate: '2024-01-31'
 * }));
 * 
 * // Fetch without date range (default behavior)
 * dispatch(fetchStudentAttendance({}));
 * ```
 * 
 * Note: If backend returns attendance in /v2/dashboard response, this thunk may be updated
 * to use the same endpoint with different parameters. For now, assuming separate endpoint.
 */
export const fetchStudentAttendance = createAsyncThunk<
  IDashboardSummaryResponse, // Reusing same response interface (has studentAttendance field)
  IGetStudentAttendanceRequestParams,
  { rejectValue: IDashboardError }
>(
  'dashboard/fetchStudentAttendance',
  async (params, { rejectWithValue }) => {
    try {
      // Call service layer for attendance with typed params
      const response = await getStudentAttendance(params || {});
      
      return response.data as IDashboardSummaryResponse;
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const error = err as AxiosError<IDashboardError>;
        const errorResponse = error.response?.data;
        
        return rejectWithValue(
          errorResponse || {
            status: error.response?.status || 500,
            message: error.message || i18n.t('toast.failed_to_fetch_student_attendance', 'Failed to fetch student attendance'),
          }
        );
      }
      
      return rejectWithValue({
        status: 500,
        message: i18n.t('msg.an_unexpected_error_occurred', i18n.t('toast.an_unexpected_error_occurred', 'An unexpected error occurred')),
      });
    }
  }
);

// ==================== Helper Functions (Optional) ====================

/**
 * Check if dashboard summary cache is still valid
 * @param lastFetched - Timestamp of last fetch
 * @param ttlMinutes - Cache TTL in minutes (default: 5)
 * @returns true if cache is valid, false otherwise
 */
export const isCacheValid = (lastFetched: number | null, ttlMinutes: number = 5): boolean => {
  if (!lastFetched) return false;
  const CACHE_TTL = ttlMinutes * 60 * 1000;
  return Date.now() - lastFetched < CACHE_TTL;
};

/**
 * Build dashboard summary request params from filters
 * Filters out null/undefined values
 */
export const buildSummaryParams = (filters: {
  curriculumId: string | null;
  gradeId: string | null;
  classroomId: string | null;
}): IGetDashboardSummaryRequestParams => {
  const params: IGetDashboardSummaryRequestParams = {};
  
  if (filters.curriculumId) params.curriculumId = filters.curriculumId;
  if (filters.gradeId) params.gradeId = filters.gradeId;
  if (filters.classroomId) params.classroomId = filters.classroomId;
  
  return params;
};

/**
 * Build student attendance request params from date range
 * Filters out null/undefined values
 */
export const buildAttendanceParams = (dateRange: {
  startDate: string | null;
  endDate: string | null;
}): IGetStudentAttendanceRequestParams => {
  const params: IGetStudentAttendanceRequestParams = {};
  
  if (dateRange.startDate) params.startDate = dateRange.startDate;
  if (dateRange.endDate) params.endDate = dateRange.endDate;
  
  return params;
};
